import AppLayout from '@/app-layout.vue';
import PageWrapper from '@/page-wrapper.vue';
import {
  DefaultLayout,
  LegalDocumentView,
  PrivacyOrganizationSettings,
  PrivacyLegalDocuments,
  PrivacyRetentionPolicy,
  AuditJournal,
} from '@fondationcss/ui';
import { WorkflowListView, WorkflowEditView, WorkflowExecutionsView } from '@fondationcss/workflow';
import type { RouteLocationNormalized } from 'vue-router';
import { GROUP_TRAINING_FEATURE } from '@/utils/group-training-feature';
import { DEMONSTRATION_FEATURE } from '@/utils/demonstration-feature';

export type ContextDropdown = 'site' | 'program' | 'group' | 'teacher' | 'users';

declare module 'vue-router' {
  interface RouteMeta {
    title?: string;
    hideFooter?: boolean;
    /**
     * Removes Parcours' PageHeader entirely (breadcrumb + context bar).
     * Reserved for the routes that mount a Fondation view: those carry their own header
     * (a PageHero shipped by @fondationcss/ui), above which the PageHeader would make a
     * second title. They therefore have no breadcrumb at all — that is the trade-off, to
     * be lifted when the Fondation hands its header back.
     * A Parcours view never sets this flag: it would lose its trail.
     */
    hidePageHeader?: boolean;
    contextToolbar?: ContextDropdown[];
    /** Shows appearance controls whose preferences are saved independently for this page. */
    teachingGridAppearance?: boolean;
    /**
     * Offers the « Style du tableau » choice (Classique / Grille) in the Affichage panel.
     * Only the Présences sheet has two renderings; the flag is inert elsewhere.
     */
    teachingGridStyles?: boolean;
    readonly?: boolean;
    groupStatusDefault?: 'confirmed' | 'unconfirmed' | 'all';
    defaultReportContext?: 'enseignement' | 'planification' | 'gestion';
  }
}

export const getCustomRoutes = () => [
  {
    path: '/',
    component: AppLayout,
    children: [
      {
        path: '',
        component: PageWrapper,
        children: [
          {
            name: 'home',
            path: '',
            component: () => import('@/views/home.vue'),
            // The only component-bearing route that carried no title: both the
            // browser tab and the breadcrumb fallback had nothing to show (#715).
            meta: { title: 'Accueil', hideFooter: true, contextToolbar: [] }
          },
          {
            name: 'docs',
            path: '/docs/:pathMatch(.*)',
            component: () => import('@/views/docs.vue'),
            meta: { title: 'Documentation', hideFooter: true, contextToolbar: [] }
          },
          {
            name: 'preferences',
            path: '/preferences',
            component: () => import('@/views/user-preferences/index.vue'),
            meta: { title: 'Préférences', hideFooter: true, contextToolbar: [] }
          },
          {
            name: 'confidentialite',
            path: '/confidentialite',
            component: LegalDocumentView,
            props: { type: 'PrivacyPolicy' },
            meta: { title: 'Politique de confidentialité', hideFooter: false, contextToolbar: [] }
          },
          {
            name: 'avis-de-collecte',
            path: '/avis-de-collecte',
            component: LegalDocumentView,
            props: { type: 'PrivacyNotice' },
            meta: { title: 'Avis de collecte', hideFooter: false, contextToolbar: [] }
          },
        ]
      },
    ]
  },
  {
    path: '/enseignement',
    component: AppLayout,
    children: [
      {
        path: '',
        component: PageWrapper,
        children: [
          // Bare root. The `redirect` lived on the parent, where it never fires: this
          // `path: ''` child captures the resolution as a leaf, PageWrapper renders with an
          // empty RouterView, and the screen shows a blank page under an empty breadcrumb.
          // The redirect must therefore live here, at the only level where `''` was free.
          // Verified in the browser afterwards: the previous fix did not work.
          { path: '', redirect: { name: 'enseignement-tableau-de-bord' } },
          {
            name: 'enseignement-tableau-de-bord',
            path: 'tableau-de-bord',
            component: () => import('@/views/teaching/dashboard.vue'),
            meta: { title: 'Tableau de bord', hideFooter: false, contextToolbar: ['site', 'program', 'teacher'] }
          },
          {
            name: 'enseignement-charge-annuelle',
            path: 'charge-annuelle',
            component: () => import('@/views/teaching/teacher-annual-load.vue'),
            meta: { title: 'Calendrier', hideFooter: true, contextToolbar: ['site', 'program', 'teacher'] }
          },
          {
            name: 'enseignement-presences',
            path: 'presences',
            component: () => import('@/views/teaching/presences.vue'),
            meta: { title: 'Présences', hideFooter: true, contextToolbar: ['site', 'program', 'group', 'teacher'], teachingGridAppearance: true, teachingGridStyles: true }
          },
          {
            name: 'enseignement-examens',
            path: 'examens',
            component: () => import('@/views/teaching/exams.vue'),
            meta: { title: 'Examens', hideFooter: true, contextToolbar: ['site', 'program', 'group', 'teacher', 'users'] , fillHeight: true}
          },
          // The student's own dashboard (#957) — the default landing of their section. Its path
          // keeps the `mon-` shape of its neighbours so nothing collides with the teacher's
          // `/enseignement/tableau-de-bord`; only the LABELS dropped the possessive.
          {
            name: 'enseignement-mon-tableau-de-bord',
            path: 'mon-tableau-de-bord',
            component: () => import('@/views/teaching/student-dashboard.vue'),
            meta: { title: 'Tableau de bord', hideFooter: true, contextToolbar: [] }
          },
          {
            name: 'enseignement-mes-examens',
            path: 'mes-examens',
            component: () => import('@/views/teaching/my-exams.vue'),
            meta: { title: 'Examens', hideFooter: true, contextToolbar: [] , fillHeight: true}
          },
          {
            name: 'enseignement-mon-horaire',
            path: 'mon-horaire',
            component: () => import('@/views/teaching/my-schedule.vue'),
            meta: { title: 'Calendrier', hideFooter: true, contextToolbar: [] }
          },
          {
            name: 'enseignement-mon-stage',
            path: 'mon-stage',
            component: () => import('@/views/teaching/my-work-placement.vue'),
            meta: { title: 'Mon stage', hideFooter: true, contextToolbar: [] , fillHeight: true}
          },
          {
            name: 'enseignement-mes-rapports',
            path: 'mes-rapports',
            component: () => import('@/views/teaching/my-reports.vue'),
            meta: { title: 'Rapports', hideFooter: true, contextToolbar: [] }
          },
          {
            name: 'enseignement-mes-justificatifs',
            path: 'mes-justificatifs',
            component: () => import('@/views/teaching/my-justifications.vue'),
            meta: { title: 'Justificatifs', hideFooter: true, contextToolbar: [] }
          },
          {
            name: 'enseignement-mon-suivi',
            path: 'mon-suivi',
            component: () => import('@/views/teaching/my-follow-up.vue'),
            meta: { title: 'Suivi', hideFooter: true, contextToolbar: [] }
          },
          {
            name: 'enseignement-assiduite',
            path: 'assiduite',
            component: () => import('@/views/teaching/attendance.vue'),
            meta: { title: 'Assiduité', hideFooter: true, contextToolbar: ['site', 'program', 'group'], programType: 'group', feature: GROUP_TRAINING_FEATURE, teachingGridAppearance: true }
          },
          {
            name: 'enseignement-progression-groupe',
            path: 'progression-groupe',
            component: () => import('@/views/teaching/group-progression.vue'),
            meta: { title: 'Progression du groupe', hideFooter: true, contextToolbar: ['site', 'program', 'group'], feature: GROUP_TRAINING_FEATURE }
          },
          {
            name: 'enseignement-progression-groupe-detail',
            path: 'progression-groupe/detail',
            component: () => import('@/views/teaching/group-progression.vue'),
            meta: { title: 'Progression du groupe', hideFooter: true, contextToolbar: [], feature: GROUP_TRAINING_FEATURE }
          },
          {
            name: 'enseignement-assignations',
            path: 'assignations',
            component: () => import('@/views/teaching/assignments.vue'),
            meta: { title: 'Assignations', hideFooter: true, contextToolbar: ['site', 'program', 'teacher'], programType: 'individual', teachingGridAppearance: true }
          },
          {
            name: 'enseignement-assignation-individuelle',
            path: 'assignation-individuelle',
            component: () => import('@/views/teaching/student-assignments.vue'),
            meta: { title: 'Assignations', hideFooter: true, contextToolbar: [] }
          },
          {
            path: 'assignations-individuelles/:frequentationId?',
            redirect: (to: RouteLocationNormalized) => ({
              path: '/enseignement/assignation-individuelle',
              hash: to.params.frequentationId ? `#id=${to.params.frequentationId}` : '',
            })
          },
          {
            name: 'enseignement-vue-consolidee',
            path: 'vue-consolidee',
            component: () => import('@/views/teaching/consolidated-view.vue'),
            meta: { title: 'Vue consolidée', hideFooter: true, contextToolbar: ['site', 'program', 'teacher'], programType: 'individual', teachingGridAppearance: true }
          },
          {
            name: 'enseignement-rapports',
            path: 'rapports',
            component: () => import('@/views/reports.vue'),
            meta: { title: 'Rapports', hideFooter: true, contextToolbar: ['site', 'program', 'group', 'teacher', 'users'], defaultReportContext: 'enseignement' }
          },
        ]
      },
    ]
  },
  {
    path: '/planification',
    component: AppLayout,
    children: [
      {
        path: '',
        component: PageWrapper,
        children: [
          // Bare root. The `redirect` lived on the parent, where it never fires: this
          // `path: ''` child captures the resolution as a leaf, PageWrapper renders with an
          // empty RouterView, and the screen shows a blank page under an empty breadcrumb.
          // The redirect must therefore live here, at the only level where `''` was free.
          // Verified in the browser afterwards: the previous fix did not work.
          { path: '', redirect: { name: 'planification-tableau-de-bord' } },
          {
            name: 'planification-disponibilites', path: 'disponibilites',
            component: () => import('@/views/planning/teacher-offerings.vue'),
            meta: { title: 'Disponibilités par compétence', contextToolbar: ['program', 'teacher'] }
          },
          {
            name: 'planification-tableau-de-bord',
            path: 'tableau-de-bord',
            component: () => import('@/views/planning/dashboard.vue'),
            meta: { title: 'Tableau de bord', hideFooter: false, contextToolbar: ['site', 'program', 'group'] }
          },
          {
            name: 'planification-simulation',
            path: 'simulation',
            component: () => import('@/views/planning/simulation.vue'),
            meta: { title: 'Solveur', hideFooter: true, contextToolbar: ['site', 'program'], feature: GROUP_TRAINING_FEATURE }
          },
          ...(import.meta.env.DEV ? [{
            name: 'planification-test-solveur-v2',
            path: 'test-solveur-v2',
            component: () => import('@/views/planning/solver-test-v2.vue'),
            meta: { title: 'Test du solveur V2 (interne)', hideFooter: true, contextToolbar: [], feature: GROUP_TRAINING_FEATURE }
          }] : []),
          {
            name: 'planification-etudiants',
            path: 'etudiants',
            component: () => import('@/views/planning/students.vue'),
            meta: { title: 'Gestion des élèves', hideFooter: true, contextToolbar: ['users'] , fillHeight: true}
          },
          {
            name: 'planification-etudiant-detail',
            path: 'etudiants/detail',
            component: () => import('@/views/planning/student.vue'),
            // No fillHeight: a form of fields, not a grid — it must be able to scroll like
            // any other form (#710, #717).
            meta: { title: 'Élève', hideFooter: true, contextToolbar: [] }
          },
          {
            path: 'etudiants/:id',
            redirect: (to: RouteLocationNormalized) => ({ path: '/planification/etudiants/detail', hash: `#id=${to.params.id}` })
          },
          {
            name: 'planification-frequentations',
            path: 'frequentations',
            component: () => import('@/views/planning/enrollments.vue'),
            meta: { title: 'Gestion des fréquentations', hideFooter: true, contextToolbar: ['site', 'program', 'group', 'users'] , fillHeight: true}
          },
          {
            name: 'planification-frequentation',
            path: 'frequentation',
            component: () => import('@/views/planning/frequentation-form.vue'),
            meta: { title: 'Fréquentation', hideFooter: true, contextToolbar: [] }
          },
          {
            path: 'frequentations/new',
            redirect: () => ({ path: '/planification/frequentation', hash: '#new=1' })
          },
          {
            path: 'frequentations/:id',
            redirect: (to: RouteLocationNormalized) => ({ path: '/planification/frequentation', hash: `#id=${to.params.id}` })
          },
          {
            name: 'planification-sequencage',
            path: 'sequencage',
            component: () => import('@/views/planning/group-sequencing.vue'),
            meta: { title: 'Horaire du groupe', hideFooter: true, contextToolbar: [], feature: GROUP_TRAINING_FEATURE }
          },
          {
            name: 'planification-sequencage-edition',
            path: 'sequencage-edition',
            component: () => import('@/views/planning/group-sequencing-editor.vue'),
            meta: { title: 'Séquence du groupe', hideFooter: true, contextToolbar: [], feature: GROUP_TRAINING_FEATURE }
          },
          {
            path: 'sequencage/:id',
            redirect: (to: RouteLocationNormalized) => ({ path: '/planification/sequencage', hash: `#id=${to.params.id}` })
          },
          {
            name: 'planification-vue-ensemble',
            path: 'vue-ensemble',
            component: () => import('@/views/planning/groups.vue'),
            meta: { title: "Groupes", hideFooter: true, contextToolbar: ['site', 'program'], fillHeight: true, feature: GROUP_TRAINING_FEATURE }
          },
          {
            name: 'planification-affectation',
            path: 'affectation',
            component: () => import('@/views/planning/group-members.vue'),
            meta: { title: 'Élèves du groupe', hideFooter: true, contextToolbar: [], fillHeight: true, feature: GROUP_TRAINING_FEATURE }
          },
          {
            path: 'affectation/:id',
            redirect: (to: RouteLocationNormalized) => ({ path: '/planification/affectation', hash: `#id=${to.params.id}` })
          },
          {
            name: 'planification-individualise',
            path: 'individualise',
            component: () => import('@/views/planning/individual-plannings.vue'),
            meta: { title: 'Élèves individualisés', hideFooter: true, contextToolbar: ['site', 'program'] , fillHeight: true}
          },
          {
            name: 'planification-individualise-detail',
            path: 'individualise/detail',
            component: () => import('@/views/planning/individual-planning.vue'),
            meta: { title: 'Élève individualisé', hideFooter: true, contextToolbar: [] }
          },
          {
            path: 'individualise/:frequentationId',
            redirect: (to: RouteLocationNormalized) => ({ path: '/planification/individualise/detail', hash: `#id=${to.params.frequentationId}` })
          },
          {
            name: 'planification-stages',
            path: 'stages',
            component: () => import('@/views/planning/work-placements.vue'),
            meta: { title: 'Stages', hideFooter: true, contextToolbar: ['site', 'program', 'group'] , fillHeight: true}
          },
          {
            name: 'planification-stage',
            path: 'stage',
            component: () => import('@/views/planning/work-placement.vue'),
            meta: { title: 'Stage', hideFooter: true, contextToolbar: [] , fillHeight: true}
          },
          {
            path: 'stages/:id',
            redirect: (to: RouteLocationNormalized) => ({ path: '/planification/stage', hash: `#id=${to.params.id}` })
          },
          {
            name: 'planification-entreprises',
            path: 'entreprises',
            component: () => import('@/views/planning/enterprises.vue'),
            meta: { title: 'Entreprises', hideFooter: true, contextToolbar: [] , fillHeight: true}
          },
          {
            name: 'planification-rapports',
            path: 'rapports',
            component: () => import('@/views/reports.vue'),
            meta: { title: 'Rapports', hideFooter: true, contextToolbar: ['site', 'program', 'group', 'teacher', 'users'], defaultReportContext: 'planification' }
          },
        ]
      },
    ]
  },
  {
    path: '/gestion',
    component: AppLayout,
    children: [
      {
        path: '',
        component: PageWrapper,
        children: [
          // Bare root. The `redirect` lived on the parent, where it never fires: this
          // `path: ''` child captures the resolution as a leaf, PageWrapper renders with an
          // empty RouterView, and the screen shows a blank page under an empty breadcrumb.
          // The redirect must therefore live here, at the only level where `''` was free.
          // Verified in the browser afterwards: the previous fix did not work.
          { path: '', redirect: { name: 'gestion-tableau-de-bord' } },
          {
            name: 'gestion-tableau-de-bord',
            path: 'tableau-de-bord',
            component: () => import('@/views/management/dashboard.vue'),
            meta: { title: 'Tableau de bord', hideFooter: false, contextToolbar: ['site', 'program'] }
          },
          {
            name: 'gestion-enseignants',
            path: 'enseignants',
            component: () => import('@/views/management/teachers.vue'),
            meta: { title: 'Enseignants', hideFooter: true, contextToolbar: ['site', 'program'] , fillHeight: true}
          },
          {
            name: 'gestion-enseignant-detail',
            path: 'enseignants/detail',
            component: () => import('@/views/management/teacher.vue'),
            // No fillHeight: a mixed record (identity/colour, affinities+schedule, load) that
            // scrolls like any other detail page (#710, #717, #719) — like
            // enseignement/charge-annuelle today, which is not fillHeight either despite its
            // own calendar + grid.
            meta: { title: 'Enseignant', hideFooter: true, contextToolbar: [] }
          },
          {
            path: 'enseignants/:id',
            redirect: (to: RouteLocationNormalized) => ({ path: '/gestion/enseignants/detail', hash: `#id=${to.params.id}` })
          },
          {
            name: 'gestion-embauches',
            path: 'embauches',
            component: () => import('@/views/management/hires.vue'),
            meta: { title: 'Embauches', hideFooter: true, contextToolbar: ['site', 'program'] , fillHeight: true}
          },
          {
            name: 'gestion-centres',
            path: 'centres',
            component: () => import('@/views/management/sites.vue'),
            meta: { title: 'Centres', hideFooter: false, contextToolbar: [] , fillHeight: true}
          },
          {
            name: 'gestion-lieux-examen',
            path: 'lieux-examen',
            component: () => import('@/views/management/exam-locations.vue'),
            meta: { title: "Lieux d'examen", hideFooter: false, contextToolbar: [], fillHeight: true }
          },
          {
            name: 'gestion-locaux-ressources',
            path: 'locaux-ressources',
            component: () => import('@/views/management/locals-resources.vue'),
            meta: { title: 'Locaux et ressources', hideFooter: false, contextToolbar: ['site'], fillHeight: true }
          },
          {
            name: 'gestion-locaux-occupation',
            path: 'locaux-ressources/occupation',
            component: () => import('@/views/management/locals-occupancy.vue'),
            meta: { title: 'Occupation des locaux', hideFooter: false, contextToolbar: ['site'] }
          },
          {
            name: 'gestion-programmes',
            path: 'programmes',
            component: () => import('@/views/management/programs.vue'),
            meta: { title: 'Programmes', hideFooter: false, contextToolbar: ['site'] }
          },
          {
            name: 'gestion-programme',
            path: 'programme',
            component: () => import('@/views/management/program.vue'),
            meta: { title: 'Programme', hideFooter: false, contextToolbar: [] }
          },
          {
            name: 'gestion-module',
            path: 'programme/module',
            component: () => import('@/views/management/module.vue'),
            meta: { title: 'Compétence', hideFooter: false, contextToolbar: [] }
          },
          {
            path: 'programmes/:programId/modules/:moduleId',
            redirect: (to: RouteLocationNormalized) => ({
              path: '/gestion/programme/module',
              hash: `#programid=${to.params.programId}&id=${to.params.moduleId}`,
            })
          },
          {
            path: 'programmes/:programId',
            redirect: (to: RouteLocationNormalized) => ({ path: '/gestion/programme', hash: `#id=${to.params.programId}` })
          },
          {
            name: 'gestion-gabarits',
            path: 'gabarits',
            component: () => import('@/views/management/templates.vue'),
            meta: { title: 'Gabarits', hideFooter: false, contextToolbar: ['site', 'program'] , fillHeight: true}
          },
          {
            name: 'gestion-references',
            path: 'references',
            component: () => import('@/views/management/references.vue'),
            meta: { title: 'Références', hideFooter: false, contextToolbar: [] , fillHeight: true}
          },
          {
            name: 'gestion-demonstration',
            path: 'demonstration',
            component: () => import('@/views/management/demo.vue'),
            // The sidebar entry already carries the flag; the route needs it too, or the page
            // still opens by deep link on an instance that turned demonstration off — with every
            // API call behind it answering 404.
            meta: { title: 'Démonstration', hideFooter: false, contextToolbar: [] , fillHeight: true, feature: DEMONSTRATION_FEATURE }
          },
          {
            name: 'gestion-confidentialite-parametres',
            path: 'confidentialite/parametres',
            component: PrivacyOrganizationSettings,
            props: {
              conservationRoute: '/gestion/confidentialite/parametres/conservation',
              documentsRoute: '/gestion/confidentialite/parametres/documents',
            },
            meta: { title: 'Paramètres de confidentialité', hideFooter: false, contextToolbar: [], hidePageHeader: true }
          },
          {
            name: 'gestion-confidentialite-documents',
            path: 'confidentialite/parametres/documents',
            component: PrivacyLegalDocuments,
            props: {
              settingsRoute: '/gestion/confidentialite/parametres',
              privacyNoticeRoute: '/avis-de-collecte',
              privacyPolicyRoute: '/confidentialite',
            },
            meta: { title: 'Documents légaux', hideFooter: false, contextToolbar: [], hidePageHeader: true }
          },
          {
            name: 'gestion-confidentialite-conservation',
            path: 'confidentialite/parametres/conservation',
            component: PrivacyRetentionPolicy,
            meta: { title: 'Conservation et suppression', hideFooter: false, contextToolbar: [], hidePageHeader: true }
          },
          {
            name: 'gestion-audit',
            path: 'audit',
            component: AuditJournal,
            meta: { title: "Journal d'audit", hideFooter: false, contextToolbar: [], hidePageHeader: true }
          },
          {
            name: 'gestion-rapports',
            path: 'rapports',
            component: () => import('@/views/reports.vue'),
            meta: { title: 'Rapports', hideFooter: true, contextToolbar: ['site', 'program', 'group', 'teacher', 'users'], defaultReportContext: 'gestion' }
          },
        ]
      },
    ]
  },
  {
    path: '/workflow',
    component: DefaultLayout,
    children: [
      {
        path: '',
        children: [
          {
            name: 'workflow-list',
            path: '',
            component: WorkflowListView,
            meta: { title: 'Flux de travail', hideFooter: true, contextToolbar: [] }
          },
          {
            name: 'workflow-edit',
            path: ':id',
            component: WorkflowEditView,
            meta: { title: 'Flux de travail', hideFooter: true, contextToolbar: [] }
          },
          {
            name: 'workflow-executions',
            path: ':id/executions',
            component: WorkflowExecutionsView,
            meta: { title: 'Historique', hideFooter: true, contextToolbar: [] }
          },
        ]
      },
    ]
  },
];
